import type { DateTime } from "luxon";
import { useCallback } from "react";
import type { WeekData } from "@/utils/models";
import { datesOfWeek } from "@/utils/panelWeeks";
import { toIsoDate } from "@/utils/scheduleWeeks";
import { getShiftTypeByShiftTimes } from "@/utils/utilFunctions";
import { useWeekViewContext } from "./WeekViewContext";

type ShiftTimes = { startTime: DateTime; endTime: DateTime };

/** The week holding a day, and that day's date as its rows carry it (rows match their days by this exact value). */
export type DayInWeek = { week: WeekData; dayDate: DateTime };

const shiftTypeInWeek = (week: WeekData, times: ShiftTimes): string =>
	getShiftTypeByShiftTimes(
		week.weeklyShifts.map((row) => row.type),
		times,
	);

/** The Day view's week, for its edits; the page renders only once that week holds the day, so it's always there. */
export const useDayWeek = (date: DateTime) => {
	const { scheduleWeeks } = useWeekViewContext();

	/** The day's week, or `undefined` while it isn't loaded, for a read that can go without it. */
	const loadedDayWeek = useCallback((): DayInWeek | undefined => {
		// Any row can hold the day: a row skips the days it has no staffing for, so the first one may not have it.
		const isoDate = toIsoDate(date);
		for (const week of scheduleWeeks) {
			const dayDate = datesOfWeek(week).find((weekDate) => toIsoDate(weekDate) === isoDate);
			if (dayDate) return { week, dayDate };
		}
		return undefined;
	}, [scheduleWeeks, date]);

	const dayWeek = useCallback((): DayInWeek => {
		const found = loadedDayWeek();
		if (!found) throw new Error(`The week of ${date.toISODate()} isn't loaded`);
		return found;
	}, [loadedDayWeek, date]);

	/** The shift type a shift with these times goes in: the start-hour guess among the week's shift types. */
	const shiftTypeOfTimes = useCallback((times: ShiftTimes): string => shiftTypeInWeek(dayWeek().week, times), [dayWeek]);

	/** `shiftTypeOfTimes`, or `undefined` while the day's week isn't loaded. */
	const loadedShiftTypeOfTimes = useCallback(
		(times: ShiftTimes): string | undefined => {
			const found = loadedDayWeek();
			return found && shiftTypeInWeek(found.week, times);
		},
		[loadedDayWeek],
	);

	return { dayWeek, loadedDayWeek, shiftTypeOfTimes, loadedShiftTypeOfTimes };
};
